import { useEffect, useState, useRef } from "react";
import { api, formatApiError, formatIDR, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, Search, Disc3, Upload, Download, Info, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const STATUS_STYLES = {
  stok: "bg-emerald-100 text-emerald-800 border border-emerald-300",
  terpasang: "bg-slate-900 text-orange-400 border border-slate-900",
  rusak: "bg-red-100 text-red-800 border border-red-300",
  pensiun: "bg-slate-200 text-slate-700 border border-slate-300",
};

export default function TyresPage() {
  const { user } = useAuth();
  const isKoor = user?.role === "koordinator";
  const canDelete = user?.role === "management" || user?.role === "admin";
  const [tyres, setTyres] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ serial_short: "", brand: "", size: "", purchase_price: "", notes: "" });
  const fileRef = useRef(null);

  const load = () => api.get("/tyres").then((r) => setTyres(r.data)).catch(() => {});
  const loadCatalog = () => api.get("/tyre-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  useEffect(() => { load(); loadCatalog(); }, []);
  useAutoRefresh(() => { load(); loadCatalog(); }, 30000);

  const brands = [...new Set(catalog.map((c) => c.brand))].sort();
  const sizesForBrand = form.brand ? [...new Set(catalog.filter((c) => c.brand === form.brand).map((c) => c.size))].sort() : [];

  const openAdd = () => {
    setEditing(null);
    setForm({ serial_short: "", brand: "", size: "", purchase_price: "", notes: "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      serial_short: t.serial_short,
      brand: t.brand,
      size: t.size,
      purchase_price: t.purchase_price ?? "",
      notes: t.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const payload = { brand: form.brand, size: form.size, notes: form.notes };
        if (!isKoor && form.purchase_price !== "") payload.purchase_price = Number(form.purchase_price);
        await api.patch(`/tyres/${editing.id}`, payload);
        toast.success("Ban diperbarui");
      } else {
        const payload = { serial_short: form.serial_short, brand: form.brand, size: form.size, notes: form.notes };
        if (!isKoor && form.purchase_price !== "") payload.purchase_price = Number(form.purchase_price);
        await api.post("/tyres", payload);
        toast.success("Ban berhasil ditambahkan ke stok");
      }
      setOpen(false);
      setEditing(null);
      setForm({ serial_short: "", brand: "", size: "", purchase_price: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/tyres/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import selesai: ${data.created} ban baru, ${data.skipped_duplicate} duplikat dilewati, ${data.errors.length} error`);
      if (data.errors.length) console.warn("Import errors:", data.errors);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("tms_token");
      const res = await fetch(`${API}/exports/tyres.xlsx`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Gagal export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "inventori-ban.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal export Excel");
    }
  };

  const filtered = tyres.filter((t) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      t.serial_short.toLowerCase().includes(s) ||
      t.brand?.toLowerCase().includes(s) ||
      t.size?.toLowerCase().includes(s) ||
      t.placement?.toLowerCase().includes(s)
    );
  });

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Ban</h1>
          <p className="text-slate-500 text-sm mt-1">Inventori ban dengan serial 5-7 digit terakhir.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
            data-testid="import-csv-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            data-testid="import-csv-btn"
            className="h-12 rounded-sm border-slate-900 border-2 font-bold uppercase tracking-wider"
          >
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            data-testid="export-xlsx-btn"
            className="h-12 rounded-sm border-slate-900 border-2 font-bold uppercase tracking-wider"
          >
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button
              onClick={openAdd}
              className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12"
              data-testid="add-tyre-btn"
            >
              <Plus className="w-5 h-5 mr-1" /> Tambah Ban
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm border-2 border-slate-900">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black">
                {editing ? "Edit Ban" : "Tambah Ban Baru ke Stok"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Serial 5-7 digit terakhir *</Label>
                <Input
                  data-testid="tyre-serial"
                  required
                  disabled={!!editing}
                  value={form.serial_short}
                  onChange={(e) => setForm({ ...form, serial_short: e.target.value.toUpperCase() })}
                  className="h-12 rounded-sm font-mono uppercase text-lg disabled:bg-slate-100"
                  placeholder="123456"
                  maxLength={7}
                  minLength={5}
                />
                {editing && <p className="text-xs text-slate-500 mt-1">Serial tidak dapat diubah setelah ban terdaftar.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Merk *</Label>
                  <Select
                    value={form.brand}
                    onValueChange={(v) => setForm({ ...form, brand: v, size: "" })}
                  >
                    <SelectTrigger className="h-12 rounded-sm" data-testid="tyre-brand">
                      <SelectValue placeholder="-- Pilih merk --" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500">Master ban belum tersedia. Hubungi management.</div>
                      ) : (
                        brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Ukuran *</Label>
                  <Select
                    value={form.size}
                    onValueChange={(v) => setForm({ ...form, size: v })}
                    disabled={!form.brand}
                  >
                    <SelectTrigger className="h-12 rounded-sm" data-testid="tyre-size">
                      <SelectValue placeholder={form.brand ? "-- Pilih ukuran --" : "Pilih merk dulu"} />
                    </SelectTrigger>
                    <SelectContent>
                      {sizesForBrand.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isKoor ? (
                <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600">
                  <Info className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
                  <div>Harga ban otomatis diambil dari master oleh sistem. Kalau kombinasi merk & ukuran belum ada di master, hubungi management.</div>
                </div>
              ) : (
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Harga Beli (Rp) — opsional</Label>
                  <Input
                    data-testid="tyre-price"
                    type="number"
                    min={0}
                    value={form.purchase_price}
                    onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                    className="h-12 rounded-sm font-mono"
                    placeholder="Kosongkan untuk pakai harga master"
                  />
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={busy || !form.brand || !form.size} data-testid="tyre-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="tyre-search"
          placeholder="Cari serial, merk, atau lokasi terpasang..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-12 rounded-sm border-slate-300"
        />
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Serial</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Merk</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Ukuran</TableHead>
              {!isKoor && <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Harga</TableHead>}
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Status</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Lokasi</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold w-24 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((t) => (
              <TableRow key={t.id} data-testid={`tyre-row-${t.serial_short}`}>
                <TableCell className="font-mono font-bold text-slate-900">{t.serial_short}</TableCell>
                <TableCell>{t.brand}</TableCell>
                <TableCell className="font-mono">{t.size}</TableCell>
                {!isKoor && <TableCell className="font-mono">{formatIDR(t.purchase_price)}</TableCell>}
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${STATUS_STYLES[t.status] || ""}`}>
                    {t.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs font-mono text-slate-600">{t.placement || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-tyre-${t.serial_short}`} title="Edit">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!confirm(`Hapus ban ${t.serial_short}?`)) return;
                        try {
                          await api.delete(`/tyres/${t.id}`);
                          toast.success("Ban dihapus");
                          load();
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}
                      data-testid={`delete-tyre-${t.serial_short}`}
                      title="Hapus"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={isKoor ? 6 : 7} className="text-center py-12 text-slate-500">
                  <Disc3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Belum ada ban.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
